"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ZeekMapsIcon from "@/components/ui/icons/ZeekMapsIcon";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { TooltipContent } from "@/components/ui/tooltip-content";
import { getNotifications, markNotificationAsRead } from "@/lib/account/user";
import { useUser } from "@/lib/auth/session";
import type { Notification } from "@/lib/supabase/types";
import { timeAgo } from "@/lib/utils";
import { Bell, CircleUserRound } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useState } from "react";

interface NotificationItem {
	id: string;
	category: string;
	icon: ReactNode;
	title: string;
	description: string;
	time: string;
	href: string;
}

export interface NotificationsMenuProps {
	placement?: "top" | "bottom" | "left" | "right";
}

function getIconForCategory(category: string): ReactNode {
	switch (category) {
		case "alerts":
			return <CircleUserRound className="w-5 h-5" />;
		case "updates":
			return <ZeekMapsIcon className="w-5 h-5" />;
		default:
			return <Bell className="w-5 h-5" />;
	}
}

export default function NotificationsMenu({
	placement = "bottom",
}: NotificationsMenuProps) {
	const { user, isUserLoading, error } = useUser();

	const [notifications, setNotifications] = useState<NotificationItem[]>([]);
	const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
	const [notificationError, setNotificationError] = useState<Error | null>(
		null,
	);

	const clearAll = async () => {
		for (const notification of notifications) {
			const { error } = await markNotificationAsRead(notification.id);
			if (error) {
				console.error("Error marking notification as read:", error);
				setNotificationError(error);
				break;
			}
		}
		setNotifications([]);
	};

	useEffect(() => {
		if (!user) return;

		const fetchNotifications = async () => {
			setIsNotificationsLoading(true);

			try {
				const { data, error } = await getNotifications(user.id);
				if (error) {
					console.error("Error fetching notifications:", error);
					setNotificationError(error);
					return;
				}

				if (!data) {
					setNotifications([]);
					return;
				}

				const fetchedNotifications: NotificationItem[] = (data || []).map(
					(notification: Notification) => ({
						id: notification.id,
						category: notification.type || "updates",
						icon: getIconForCategory(notification.type),
						title: notification.title,
						description: notification.message,
						time: timeAgo(new Date(notification.created_at)),
						href: notification.action_url || "#",
					}),
				);

				setNotifications(fetchedNotifications);
			} catch (err) {
				const error =
					err instanceof Error
						? err
						: new Error("Failed to load notifications");
				console.error("Error loading notifications:", error);
				setNotificationError(error);
			} finally {
				setIsNotificationsLoading(false);
			}
		};

		fetchNotifications();
	}, [user]);

	if (isUserLoading) {
		return <Spinner className="size-3 self-center" />;
	}

	if (error) {
		console.error(error);
		return null;
	}

	if (!user) return null;

	return (
		<Popover>
			<TooltipContent content="Notifications">
				<PopoverTrigger asChild>
					<button
						className="relative inline-flex items-center justify-center rounded-full p-2 hover:bg-primary/20"
						type="button"
					>
						<>
							<Bell className="text-primary" size={20} />
							{notifications.length > 0 && (
								<Badge
									variant="default"
									className="absolute -top-1 -right-1 text-xs px-1 py-0 rounded-full bg-red-500 text-white"
								>
									{notifications.length}
								</Badge>
							)}
						</>
					</button>
				</PopoverTrigger>
			</TooltipContent>

			<PopoverContent
				side={placement}
				align="center"
				className="w-80 mr-10 p-0"
			>
				{/* Header with filter */}
				<div className="flex justify-between items-center border-b p-2">
					<h2 className="text-sm font-medium flex items-center gap-2">
						Notifications
					</h2>
					<div className="flex items-center gap-1">
						{notifications.length > 0 ? (
							<Button
								variant={"ghost"}
								size="sm"
								onClick={clearAll}
								className="text-xs p-2"
							>
								Clear All
							</Button>
						) : null}
					</div>
				</div>

				{isNotificationsLoading ? (
					<div className="p-4 flex justify-center">
						<Spinner className="size-4" />
					</div>
				) : notificationError ? (
					<div className="p-4 text-sm text-red-500 text-center">
						Failed to load notifications.
					</div>
				) : null}

				{/* Notifications list */}
				{notifications.length === 0 ? (
					<div className="p-4 text-sm text-muted-foreground text-center">
						All caught up! No new notifications.
					</div>
				) : (
					<div className="max-h-80 overflow-y-auto divide-y divide-border">
						{notifications.map((item: NotificationItem) => (
							<div key={item.id} className="p-4 hover:bg-muted transition">
								<Link href={item.href} className="block">
									<div className="flex justify-between items-center mb-1">
										<div className="flex items-center gap-2">
											{item.icon}
											<span className="font-medium text-sm">{item.title}</span>
										</div>
									</div>
									<p className="text-xs text-muted-foreground leading-relaxed">
										{item.description}
									</p>
									<span className="block text-xs w-full text-right justify-end items-end text-muted-foreground">
										{item.time}
									</span>
								</Link>
							</div>
						))}
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
